<template>
  <component
    :is="to ? AppLocaleLink : href ? 'a' : 'button'"
    :to="to"
    :href="href"
    :type="to || href ? undefined : type"
    :target="target"
    :disabled="disabled || loading"
    class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none uppercase font-bold transition disabled:cursor-not-allowed disabled:opacity-30"
    :class="[sizeClass, variantClass]"
  >
    <span
      v-if="loading"
      class="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent"
    />
    <slot />
  </component>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import AppLocaleLink from '@@/shared-components/app/locale/AppLocaleLink.vue'

const props = withDefaults(
  defineProps<{
    /** solid = filled, outline = bordered */
    variant?: 'solid' | 'outline'
    /** tone of the surface the button sits on: dark text vs light text */
    tone?: 'dark' | 'light' | 'brand'
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    to?: RouteLocationRaw
    href?: string
    target?: string
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
  }>(),
  { variant: 'solid', tone: 'dark', size: 'md', type: 'button' }
)

const sizeClass = computed(
  () =>
    ({
      xs: 'px-3 py-1.5 text-[10px]',
      sm: 'px-5 py-2 text-[11px]',
      md: 'px-6 py-3 text-sm',
      lg: 'px-9 py-3.5 text-base',
      xl: 'px-11 py-4 text-lg',
    })[props.size]
)

const variantClass = computed(() => {
  const { variant, tone } = props
  if (tone === 'light') {
    // sits on a dark / photo surface → light foreground
    return variant === 'solid'
      ? 'border-2 border-(--brand-ink)  bg-white text-(--brand-ink) hover:bg-white'
      : 'border-2 border-white text-white hover:bg-white hover:text-black'
  }
  if (tone === 'brand') {
    // sits on a dark / photo surface → light foreground
    return variant === 'solid'
      ? 'border-2 border-(--brand-taupe)  bg-(--brand-taupe) text-white hover:bg-(--brand-taupe)'
      : 'border-2 border-(--brand-taupe) text-(--brand-taupe) hover:bg-(--brand-taupe) hover:text-white'
  }
  return variant === 'solid'
    ? 'border-2 border-(--brand-ink)  bg-(--brand-ink) text-white hover:bg-(--brand-black)'
    : 'border-2 border-(--brand-ink) text-(--brand-ink) hover:bg-(--brand-ink) hover:text-white'
})
</script>
